// Results copy the page falls back to when the API's wording version predates Addendum C
// (wording 1.0 has no display.sections and no gift "today" lines). Word for word from
// Taylor's doc, Addendum C (docs/source/taylor-question-bank-v3-2026-10-15-addendum-c.txt).
// Wording 1.1 and later send the same text from the API, which always wins.

import type { ResultDisplay, SectionsCopy } from "./types";

/**
 * Recorded on each take's consent: which terms and privacy notice they agreed to. Novos's Terms,
 * and since Oct 26 Gifts Profile's own privacy notice (screens/Privacy.tsx), which adds to Novos's
 * policy. Takes before then agreed to "novos-2026-10-20" (Novos's Terms and Privacy Policy).
 */
export const POLICY_VERSION = "gp-2026-10-26";
export const TERMS_URL = "https://novosnetwork.com/tos/";
/** The Gifts Profile Privacy Notice: what people agree to on the start form. */
export const PRIVACY_URL = "/privacy/profile";
/** Where people get help, shown on every page (components/AppFooter.tsx). */
export const SUPPORT_EMAIL = "support@novosnetwork.com";
/** The Website Privacy Notice: cookies and ad tracking, for the cookie panel. */
export const WEBSITE_PRIVACY_URL = "/privacy";
/** Novos's own policy, which both notices add to. */
export const NOVOS_PRIVACY_URL = "https://novosnetwork.com/privacy/";
export const SOURCE_PATTERN = /^[a-z0-9_]{1,32}$/;
/** Rater link codes (the API's invite code pattern). */
export const REF_PATTERN = /^[A-Za-z0-9_-]{16,64}$/;
/** A scoring version in a start link (/?v=4 or /go/{slug}?v=4): one to three digits, anything else is ignored. */
export const VERSION_PATTERN = /^\d{1,3}$/;

/** The scoring version a start link asks for, or undefined (the API then starts the default one). */
export const versionFromParam = (raw: string | null): number | undefined => (raw && VERSION_PATTERN.test(raw) ? Number(raw) : undefined);

/**
 * The line under the start heading, per scoring version. The default is v4's (since Oct 21,
 * 2026): Taylor gave no time estimate for it, so it has none. `?v=1` keeps version 1's line.
 */
export const DEFAULT_INTRO_LINE = "38 quick questions.";
const INTRO_LINES: Record<number, string> = { 1: "About 15 minutes · 53 quick questions." };
export const introLine = (scoringVersion?: number): string =>
  (scoringVersion === undefined ? undefined : INTRO_LINES[scoringVersion]) ?? DEFAULT_INTRO_LINE;

/** Scoring v4, Part 4 (pick the statement most like you, then the one least like you). Drafted wording. */
export const TRIAD_COPY = {
  heading: "Pick the one most like you, then the one least like you.",
  statusMost: "Most like you: tap one.",
  statusLeast: "Least like you: tap one of the other two.",
  tagMost: "Most like you",
  tagLeast: "Least like you",
} as const;

export const FALLBACK_SECTIONS: SectionsCopy = {
  freedomClarity: {
    whyTitle: "Why it matters.",
    why: "Most profiles tell you what you're good at. This one starts by asking whether you're free to use what you’re naturally good at. Freedom measures how much your past, your fears and your old agreements are still steering your decisions. Clarity measures how well you know how you're built and whether your life is pointed in the direction of your choosing. Together they show where you are today, and that's the lens for everything else on this page.",
    howTitle: "How it shapes your results.",
    how: "Your gifts don't change based on where you land on the map, but how they show up does. When you're free, your gifts flow from rest: you serve, lead and create because it's who you are. When you're bound, the same gifts get pulled into proving yourself, and a gift for connection turns into people-pleasing or a gift for vision turns into overthinking. That's why your profile name and description change with your quadrant. Clarity works the same way: high freedom without clarity is energy with no target, and high clarity without freedom is a plan you can't step into.",
  },
  domain: {
    whyTitle: "Why it matters.",
    why: "Your domain is your default way of engaging the world: the move you make before you think about it. Strategic people think it through. Relating people connect. Executing people get it done. Influencing people move others. Nobody is going to be limited to just one, but everyone has a home base, and knowing yours explains why some work energizes you and other work drains you even when you're good at both.",
    howTitle: "How it affects your gifts.",
    how: "Your domain is the channel your gifts flow through. Two people can share the same gift and express it completely differently: a teacher with a Strategic home base builds frameworks, while a teacher with a Relating home base teaches one person at a time over coffee. Your domain also points to your blind spot. The domain you picked least is usually the one you skip under pressure, and that's often where your gifts stall out.",
  },
  gifts: {
    whyTitle: "Why they matter.",
    why: "Your domain is how you engage; your gifts are what you carry into the room. They're specific capacities that make other people's lives better when you use them, and they tend to show up whether or not you've named them. The ten measured here come from two ancient lists, Romans 12 and Ephesians 4, combined because those lists were examples rather than the full set. Your top three are where you'll have the most impact with the least strain.",
    howTitle: "How they show up today.",
    how: "None of these gifts require a platform or a pulpit. They show up in companies, families, teams and friendships every day.",
  },
};

export const FALLBACK_GIFTS_TODAY: Record<string, string> = {
  Apostle: "The founder, the planter, the person who builds the first version of something and then wants to start the next one.",
  Teacher: "The one who turns a mess of information into a framework, a course, a podcast or a whiteboard that finally makes it click.",
  Serving: "The person who notices what needs doing before anyone asks, and has quietly done it by the time you do.",
  Giving: "The friend who picks up the check, funds the idea, or opens their network and home without keeping score.",
  Leading: "The operator who runs the project, the team or the household so well that everything just works.",
  Shepherd: "The manager, coach or mentor who watches over specific people for years and notices when one drifts.",
  Mercy: "The one people call when it all falls apart, because they can sit in the hard thing without rushing to fix it.",
  Prophet: "The voice in the room that names what everyone senses and nobody says, even when it costs them.",
  Evangelist: "The connector who gets people excited about an idea, a product, a cause or a faith, and brings new people in.",
  Exhorter: "The coach in your corner who sees your potential before you do and won't let you settle.",
};

export const RATER_SUGGESTION =
  "We recommend sending one to a family member, one to a close friend, and one to a work colleague or professional acquaintance who knows you, but anyone who knows you well will work.";

/**
 * The "ask three people" card at the top of the results (Edward, Oct 31: "It compares you versus
 * what they say about you. So it'll expose gifts that maybe you didn't know you had, and it will
 * show you where you might feel you have a gifting that other people haven't experienced yet").
 */
export const ASK_THREE_COPY = {
  eyebrow: "Your next step",
  heading: (done: number) =>
    done === 0
      ? "Now ask 3 people who know you well"
      : done === 1
        ? "Nice, someone has already answered!"
        : "Congratulations, 2 people have answered!",
  why: "Here's the fun part. Your profile puts how you see yourself right next to how the people who know you see you, and that's usually where the surprises are. It can show you:",
  shows: [
    { title: "Hidden strengths", text: "gifts other people see in you that you might not know you have." },
    { title: "Gifts still waiting", text: "gifts you know are in you that others haven't had a chance to see yet." },
    { title: "Confirmed gifts", text: "the gifts you and they both see." },
  ],
  progressText: (done: number) =>
    done === 1
      ? "Go ahead and send your other links. As soon as one more person finishes, you'll get to see what the people who know you see in you."
      : "Your first look at what other people see in you is unlocked below. Get one more person to finish, and you'll have the full picture.",
  backTo: (name: string) => `Why not start with ${name}? You just answered questions about them, so send them one of these and they can return the favor.`,
} as const;

/**
 * What a rater link is sent with: in the taker's voice, since it goes from their phone. Answers
 * are shown combined, but not "only ever": with two in and then three, the difference is the third
 * person's (Nov 2 review), so it doesn't promise more than that.
 */
export const RATER_SHARE_MESSAGE =
  "Hey! I just took the Gifts Profile, and part of it is asking a few people who know me well what they see in me. Would you answer 18 quick questions about me? It only takes about 4 minutes, and your answers get combined with everyone else's. Thank you!";

/** Who each rater link is for (Taylor, Oct 23). Short, so the three cards read alike. */
export const RATER_LINK_FOR: Record<number, string> = { 1: "A family member", 2: "A close friend", 3: "Someone from work" };

export const sectionsFor = (d: ResultDisplay): SectionsCopy => d.sections ?? FALLBACK_SECTIONS;

/** The gift's one-liner ("Meets pain in the moment"), or its "today" line where v1 has none. */
export function giftLine(d: ResultDisplay, code: string): string {
  const g = d.gifts.find((x) => x.code === code);
  return g?.line || giftToday(d, code);
}

/** The gift's "how it shows up today" line: API first, then the doc, then the short line. */
export function giftToday(d: ResultDisplay, code: string): string {
  const g = d.gifts.find((x) => x.code === code);
  return g?.today || FALLBACK_GIFTS_TODAY[code] || g?.line || "";
}

/**
 * The rater's "take your own" sales page (Edward, Oct 26: a mini sales page after they finish
 * rating someone, since they may not know what the Gifts Profile is). Borrowed from the home
 * page (site/funnels/free-test/landing), with today's question count. Drafted, pending Taylor.
 */
export const RATER_OFFER_COPY = {
  eyebrow: "Your turn",
  cta: "Take my Gifts Profile",
  /** Above the fold, scrolls down to the "Your turn" card (Edward, Nov 3: a CTA up top; the rest can read on). */
  jump: "Discover my own gifts",
  meta: "38 quick questions · your profile is ready the moment you finish",
  /** The idea first (Edward, Nov 3: "sell the fact that your giftings don't actually change, but how you use them does"). */
  ideaTitle: "Your gifts are already there. What changes is whether you're free to use them.",
  ideaText:
    "The gifts you were made with don't really change. What changes is how free you are to use them, or whether old fears and expectations end up using them for you. When you're free, your gifts flow from rest. When you're not, the same gifts get pulled into proving yourself.",
  mapNote: "Everyone lands somewhere on this map, and it can move. Your profile shows where you are today, and what to do next.",
  /** The four places on the map, as the homepage has them, in grid order: free row (unclear, clear), then bound. */
  quadrants: [
    { name: "Drifting", line: "Free, but not yet clear. Energy with no target." },
    { name: "Activated", line: "Free and clear. Your gifts flow from rest." },
    { name: "Fragmented", line: "Neither free nor clear yet. Pulled in every direction." },
    { name: "Caged", line: "Clear, but not yet free. A plan you can't step into." },
  ],
  layersTitle: "Three layers, one picture of you.",
  layers: [
    {
      title: "Freedom & Clarity",
      text: "Freedom measures how much your past, your fears and your old agreements are still steering your decisions. Clarity measures how well you know how you're built and whether your life is pointed in the direction of your choosing.",
    },
    {
      title: "Your domain",
      text: "Your default way of engaging the world: the move you make before you think about it. It explains why some work energizes you and other work drains you, even when you're good at both.",
    },
    {
      title: "Your top gifts",
      text: "Specific capacities that make other people's lives better when you use them. Your top three are where you'll have the most impact with the least strain.",
    },
  ],
} as const;

/**
 * The rater's finish screen (Edward, Oct 31: "make it clear what they just did, and how and why to
 * see their own"; Nov 3: they "don't know what they did for Jordan… they can't even visualize
 * anything they're going to get", so explain it before the price). Thank you; the idea and the
 * map; the three layers; what their answers give the person; then their own, and "later".
 */
export const RATER_FINISH_COPY = {
  eyebrow: "All done",
  heading: (name: string) => `Thank you! You just helped ${name} discover their gifts.`,
  helpedTitle: (name: string) => `And here's the part you just helped ${name} with`,
  helpedText: (name: string) =>
    `After ${name} took their Gifts Profile, they asked a few people who know them, like you, to describe them. When your answers sit next to how ${name} sees themselves, they get:`,
  shows: [
    { title: "Hidden strengths", text: "gifts other people see in them that they might not see themselves." },
    { title: "Confirmed gifts", text: "the gifts they and the people around them both see." },
    { title: "Gifts still waiting", text: "gifts they know are in them that others haven't had a chance to see yet." },
  ],
  turnHeading: "Ever wonder what the people who know you would say about you?",
  turnText: "Now it's your turn. Take your own Gifts Profile, then send it to three people who know you well and see what they see in you.",
  backTo: (name: string) => `You could even start with ${name} and let them return the favor.`,
  laterTitle: "Want to take it later?",
  laterText: "No problem. Just pop your email in below and we'll send you the link.",
  laterLabel: "Your email",
  laterButton: "Email me the link",
  laterConsent: "Also send me occasional emails from Novos",
  laterSent: "Done! Check your inbox for your link.",
  laterNote: "We'll only send you this one email, unless you tick the box.",
} as const;

/** The start page for a rater taking their own (from their finish screen, or the link we emailed). */
export const RATER_START = {
  kicker: "Your turn",
  message: "This time it's all about you. Answer for yourself, then send it to three people who know you and find out what they see in you.",
} as const;

/** The rater's intro screen, under the instructions: plants their own test before they start. */
export const RATER_INTRO_AFTER = "Once you're done, you'll also get the chance to see what your own Gifts Profile says about you.";
